import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import API, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Loader2, Upload, X, FileText, Image as ImageIcon, ArrowLeft, Save, Lock } from "lucide-react";
import { toast } from "sonner";
import { fileToDataUrl } from "@/lib/qa";

// Feature flag: nascondi temporaneamente la sezione allegati nel form.
// Per riattivare: cambia in `true`. Nessun'altra modifica necessaria.
// Backend/API/upload/state/files/handleSubmit restano invariati.
const SHOW_ATTACHMENTS = false;

const MAX_FILES = 5;
const MAX_BYTES = 10 * 1024 * 1024;
const ALLOWED = ["image/jpeg", "image/png", "application/pdf"];

function deriveTitleFromBody(body) {
    const clean = (body || "").trim().replace(/\s+/g, " ");
    if (!clean) return "";
    if (clean.length <= 80) return clean;
    const slice = clean.slice(0, 80);
    const lastSpace = slice.lastIndexOf(" ");
    return (lastSpace > 20 ? slice.slice(0, lastSpace) : slice) + "…";
}

export default function NewQuestion() {
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const draftId = params.get("draft") || null;
    const [categories, setCategories] = useState([]);
    const [body, setBody] = useState("");
    const [files, setFiles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [currentDraftId, setCurrentDraftId] = useState(draftId);
    const [lastSavedAt, setLastSavedAt] = useState(null);

    useEffect(() => {
        API.get("/categories").then(({ data }) => setCategories(data)).catch(() => {});
    }, []);

    // Load existing draft when editing
    useEffect(() => {
        if (!draftId) return;
        API.get(`/questions/${draftId}`)
            .then(({ data }) => {
                if (data.status !== "draft") {
                    toast.error("Questa richiesta non è più modificabile.");
                    navigate("/dashboard/richieste");
                    return;
                }
                setBody(data.body || "");
                setFiles(data.attachments || []);
                setLastSavedAt(data.updated_at);
            })
            .catch(() => toast.error("Bozza non trovata"));
    }, [draftId, navigate]);

    const addFiles = async (list) => {
        const arr = Array.from(list);
        if (files.length + arr.length > MAX_FILES) { toast.error(`Massimo ${MAX_FILES} file`); return; }
        for (const f of arr) {
            if (!ALLOWED.includes(f.type)) { toast.error(`${f.name}: formato non supportato (JPG, PNG, PDF)`); continue; }
            if (f.size > MAX_BYTES) { toast.error(`${f.name}: massimo 10MB`); continue; }
            const dataUrl = await fileToDataUrl(f);
            setFiles(prev => [...prev, { filename: f.name, mime_type: f.type, size_bytes: f.size, data_url: dataUrl }]);
        }
    };

    const missing = [];
    if (body.trim().length < 10) missing.push(`descrizione di almeno 10 caratteri (${body.trim().length}/10)`);
    if (categories.length === 0) missing.push("categorie non ancora caricate, attendi un istante");
    const canSubmit = missing.length === 0 && !loading && !saving;

    const buildPayload = (status) => {
        const autoTitle = deriveTitleFromBody(body);
        const defaultCategoryId = categories[0]?.id;
        return {
            category_id: defaultCategoryId,
            title: autoTitle.length >= 5 ? autoTitle : `${autoTitle} — richiesta`,
            body,
            attachments: files,
            status,
        };
    };

    const saveDraft = async () => {
        setError(""); setSaving(true);
        try {
            const payload = buildPayload("draft");
            if (currentDraftId) {
                const { data } = await API.patch(`/questions/${currentDraftId}`, payload);
                setLastSavedAt(data.updated_at);
            } else {
                const { data } = await API.post("/questions", payload);
                setCurrentDraftId(data.id);
                setLastSavedAt(data.updated_at);
                // Reflect draft id in URL so the user can reopen
                navigate(`/dashboard/nuova?draft=${data.id}`, { replace: true });
            }
            toast.success("Bozza salvata");
        } catch (err) { setError(apiError(err)); }
        finally { setSaving(false); }
    };

    const doSubmit = async () => {
        setConfirmOpen(false);
        setError(""); setLoading(true);
        try {
            const payload = buildPayload("received");
            if (currentDraftId) {
                await API.patch(`/questions/${currentDraftId}`, payload);
            } else {
                await API.post("/questions", payload);
            }
            toast.success("Domanda inviata! Ti risponderemo appena possibile.");
            navigate("/dashboard/richieste");
        } catch (err) { setError(apiError(err)); }
        finally { setLoading(false); }
    };

    return (
        <div className="mx-auto max-w-2xl px-4 sm:px-6 lg:px-8 py-10">
            <Button asChild variant="ghost" size="sm" className="-ml-2 mb-3 text-muted-foreground">
                <Link to="/dashboard"><ArrowLeft className="size-4 mr-1" /> Dashboard</Link>
            </Button>
            <h1 className="font-serif text-3xl font-semibold" data-testid="newq-title">
                {currentDraftId ? "Modifica bozza" : "Fai una nuova domanda"}
            </h1>
            <p className="mt-2 text-muted-foreground">Ti risponderà un professionista reale. Nessuna AI. La tua richiesta resta privata.</p>
            {lastSavedAt && (
                <p className="mt-2 text-xs text-muted-foreground" data-testid="newq-last-saved">
                    Ultimo salvataggio: {new Date(lastSavedAt).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                </p>
            )}

            <Card className="mt-6 border-slate-200">
                <CardContent className="pt-6">
                    <form onSubmit={(e) => { e.preventDefault(); if (canSubmit) setConfirmOpen(true); }} className="space-y-5" data-testid="newq-form">
                        {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}
                        <div className="space-y-2">
                            <Label htmlFor="body">Descrizione <span className="text-xs text-muted-foreground">({body.length}/10000)</span></Label>
                            <Textarea id="body" value={body} onChange={e => setBody(e.target.value)} maxLength={10000} rows={7} data-testid="newq-body" placeholder="Descrivi la situazione con più contesto possibile" />
                        </div>
                        {SHOW_ATTACHMENTS && (
                        <div className="space-y-2">
                            <Label>Allegati opzionali (max 5, 10MB, JPG/PNG/PDF)</Label>
                            <label className="flex items-center justify-center gap-2 rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 hover:bg-slate-100 cursor-pointer py-6 text-sm text-slate-600" data-testid="newq-upload">
                                <Upload className="size-4" /> Aggiungi file
                                <input type="file" multiple accept=".jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf" className="hidden" onChange={e => addFiles(e.target.files)} />
                            </label>
                            {files.length > 0 && (
                                <ul className="space-y-2">
                                    {files.map((f, i) => (
                                        <li key={i} className="flex items-center justify-between rounded-md border border-slate-200 bg-white px-3 py-2 text-sm">
                                            <div className="flex items-center gap-2 min-w-0">
                                                {f.mime_type === "application/pdf" ? <FileText className="size-4 text-rose-500 shrink-0" /> : <ImageIcon className="size-4 text-sky-500 shrink-0" />}
                                                <span className="truncate">{f.filename}</span>
                                                <span className="text-xs text-muted-foreground shrink-0">{(f.size_bytes / 1024).toFixed(0)} KB</span>
                                            </div>
                                            <button type="button" onClick={() => setFiles(files.filter((_, j) => j !== i))} className="text-muted-foreground hover:text-destructive"><X className="size-4" /></button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                        )}
                        <div className="rounded-2xl border-2 border-primary/25 bg-gradient-to-br from-primary/[0.06] to-accent/[0.03] p-5 lg:p-6" data-testid="newq-privacy">
                            <div className="flex items-start gap-4">
                                <div className="size-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
                                    <Lock className="size-5" strokeWidth={2.2} />
                                </div>
                                <div className="min-w-0">
                                    <div className="font-serif text-base lg:text-lg font-semibold text-slate-900">
                                        🔒 Privacy garantita
                                    </div>
                                    <p className="mt-1 text-sm text-slate-700 leading-relaxed">
                                        La tua domanda resta <strong className="text-primary">privata</strong>.
                                        Solo tu e il professionista che se ne occupa possono visualizzarla e gestirla.
                                        Nessun altro utente può vedere il contenuto della tua richiesta.
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="flex flex-col gap-3 pt-3 border-t">
                            {missing.length > 0 && (
                                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2" data-testid="newq-missing">
                                    Per inviare la domanda: {missing.join(" · ")}.
                                </p>
                            )}
                            <div className="flex flex-col-reverse sm:flex-row sm:justify-between gap-3">
                                <Button type="button" variant="outline" onClick={saveDraft} disabled={saving || body.trim().length < 10} data-testid="newq-save-draft">
                                    {saving ? <Loader2 className="size-4 mr-2 animate-spin" /> : <Save className="size-4 mr-2" />}
                                    Salva come bozza
                                </Button>
                                <div className="flex justify-end gap-3">
                                    <Button type="button" variant="ghost" onClick={() => navigate("/dashboard")}>Annulla</Button>
                                    <Button type="submit" disabled={!canSubmit} data-testid="newq-submit">
                                        {loading && <Loader2 className="size-4 mr-2 animate-spin" />}
                                        Invia
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </form>
                </CardContent>
            </Card>

            <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
                <AlertDialogContent data-testid="newq-confirm-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Sei sicuro di voler inviare questa domanda?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Una volta inviata, la richiesta sarà presa in carico e non potrà più essere modificata.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="newq-confirm-cancel">Annulla</AlertDialogCancel>
                        <AlertDialogAction onClick={doSubmit} data-testid="newq-confirm-submit">Invia domanda</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
